import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./ServicesPreviewCell.css";

const EMPTY = "—";
const MAX_CHARS = 50;

/**
 * Services text truncated at 50 chars. Only when truncated is it a link;
 * click opens a modal listing full service names, one per line.
 */
export default function ServicesPreviewCell({
  services,
  title = "Services",
  className = "",
}) {
  const [open, setOpen] = useState(false);

  const list = (() => {
    if (!services || services === EMPTY) return [];
    if (Array.isArray(services)) {
      return services.map((s) => String(s || "").trim()).filter(Boolean);
    }
    return String(services)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  })();

  const isEmpty = list.length === 0;
  const joined = list.join(", ");
  const truncated = !isEmpty && joined.length > MAX_CHARS;
  const display = truncated
    ? `${joined.slice(0, MAX_CHARS).trimEnd()}…`
    : joined;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const openModal = (e) => {
    if (!truncated) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(true);
  };

  const closeModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(false);
  };

  return (
    <>
      <div
        className={`services-preview-cell${isEmpty ? " is-empty" : ""}${truncated ? " is-interactive" : ""}${className ? ` ${className}` : ""}`}
      >
        {isEmpty ? (
          <span className="services-preview-text muted">{EMPTY}</span>
        ) : truncated ? (
          <button
            type="button"
            className="services-preview-link"
            onClick={openModal}
            title="View services"
          >
            <span className="services-preview-text">{display}</span>
          </button>
        ) : (
          <span className="services-preview-text">{display}</span>
        )}
      </div>
      {open &&
        truncated &&
        createPortal(
          <div className="services-preview-modal-overlay" onClick={closeModal}>
            <div
              className="services-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="services-preview-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="services-preview-modal-header">
                <h3 id="services-preview-modal-title">{title}</h3>
                <button
                  type="button"
                  className="services-preview-modal-close"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div className="services-preview-modal-body">
                <ul className="services-preview-list">
                  {list.map((name, idx) => (
                    <li key={`${name}-${idx}`} className="services-preview-list-item">
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
